import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import api from "../../utils/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Plus,
  Search,
  FileText,
  ShieldCheck,
  GraduationCap,
  CalendarDays,
  Pencil,
  Copy,
  Trash2,
  LayoutTemplate,
  Info,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

interface OnboardingTemplate {
  id: number;
  name: string;
  description: string;
  status: "Published" | "Draft";
  department?: string;
  departmentId?: number | null;
  formsCount: number;
  policiesCount: number;
  learningCount: number;
  activitiesCount: number;
  assignedTo: string[];
  updatedAt: string;
}

const OnboardingTemplates: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();

  const [templates, setTemplates] = useState<OnboardingTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<OnboardingTemplate[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [cloningId, setCloningId] = useState<number | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<OnboardingTemplate | null>(null);

  const canView = ability.can(ACTIONS.READ, RESOURCES.ONBOARDING_DASHBOARD);
  const canCreate = ability.can(ACTIONS.CREATE, RESOURCES.ONBOARDING_DASHBOARD);
  const canDelete = ability.can(ACTIONS.DELETE, RESOURCES.ONBOARDING_DASHBOARD);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }

    const fetchTemplates = async () => {
      try {
        const res = await api.get<{ success: boolean; data: OnboardingTemplate[] }>(
          "/api/onboarding/templates",
          { skipErrorToast: true }
        );

        if (res.success && res.data) {
          setTemplates(res.data);
          setFilteredTemplates(res.data);
        } else {
          toast.error("Failed to load templates");
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
        toast.error("Failed to load templates");
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [canView]);

  // Apply search filter
  useEffect(() => {
    let result = [...templates];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(query) ||
          (t.description || "").toLowerCase().includes(query) ||
          (t.assignedTo || []).some((role) => role.toLowerCase().includes(query))
      );
    }

    setFilteredTemplates(result);
  }, [searchQuery, templates]);

  const handleDelete = async () => {
    if (!templateToDelete) return;
    if (!canDelete) {
      toast.error("You don't have permission to delete templates");
      setShowDeleteDialog(false);
      return;
    }

    const id = templateToDelete.id;
    setDeletingId(id);
    try {
      const res = await api.delete<{ success: boolean; message: string }>(
        `/api/onboarding/templates/${id}`
      );

      if (res.success) {
        toast.success("Template deleted successfully");
        setTemplates((prev) => prev.filter((t) => t.id !== id));
        setFilteredTemplates((prev) => prev.filter((t) => t.id !== id));
      } else {
        toast.error(res.message || "Failed to delete template");
      }
    } catch (error: any) {
      console.error("Error deleting template:", error);
      toast.error(error.message || "Failed to delete template");
    } finally {
      setDeletingId(null);
      setShowDeleteDialog(false);
      setTemplateToDelete(null);
    }
  };

  const openDeleteDialog = (template: OnboardingTemplate) => {
    setTemplateToDelete(template);
    setShowDeleteDialog(true);
  };

  const handleDuplicate = async (template: OnboardingTemplate) => {
    setCloningId(template.id);
    try {
      // First, fetch the full template data including all tab configurations
      const fullRes = await api.get<{ success: boolean; data: any }>(
        `/api/onboarding/templates/${template.id}`,
        { skipErrorToast: true }
      );

      const source = fullRes.success && fullRes.data ? fullRes.data : template;

      const payload = {
        name: `${template.name} (Copy)`,
        description: source.description,
        department: source.department || null,
        departmentId: source.departmentId ?? null,
        status: "Draft",
        assignedTo: source.assignedTo || [],
        forms: source.forms || null,
        policies: source.policies || null,
        learning: source.learning || null,
        supportNetwork: source.supportNetwork || null,
        licences: source.licences || null,
        requirements: source.requirements ?? null,
      };

      const res = await api.post<{ success: boolean; data: OnboardingTemplate; message: string }>(
        "/api/onboarding/templates",
        payload
      );

      if (res.success && res.data) {
        toast.success("Template cloned successfully");
        setTemplates((prev) => [...prev, res.data]);
        setFilteredTemplates((prev) => [...prev, res.data]);
      } else {
        toast.error(res.message || "Failed to clone template");
      }
    } catch (error: any) {
      console.error("Error cloning template:", error);
      toast.error(error.message || "Failed to clone template");
    } finally {
      setCloningId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (!canView) {
    return <AccessRestricted />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-900" />
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button
          onClick={() => navigate("/onboarding")}
          className="hover:text-gray-900 transition-colors"
        >
          Onboarding
        </button>
        <span>/</span>
        <span className="text-gray-900 font-medium">Templates</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Onboarding Templates
          </h1>
          <p className="text-gray-500 text-sm font-medium">
            Create and manage onboarding profiles that define the experience for each new hire
          </p>
        </div>
        {canCreate && (
          <Button
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl gap-2"
            onClick={() => navigate("/onboarding/templates/new")}
          >
            <Plus className="w-4 h-4" />
            New Template
          </Button>
        )}
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-5 flex items-start gap-4">
        <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
          <Info className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-blue-900 mb-1">
            What is an Onboarding Template?
          </h3>
          <p className="text-sm text-blue-700 leading-relaxed">
            A template is a reusable onboarding profile that defines exactly what a new hire will see — which forms to complete, policies to acknowledge, learning modules to complete, who their support network is, and their First Day, First Week, First Month and 30-60-90 Day plan. Assign a template when you commence hiring and the new hire's portal is built automatically.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white border-gray-200 rounded-xl h-11 max-w-md"
        />
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.length === 0 ? (
          <div className="col-span-full text-center py-16 text-gray-500">
            <LayoutTemplate className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No templates found</p>
            <p className="text-sm">Create your first onboarding template to get started</p>
          </div>
        ) : (
          filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-md transition-shadow"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-base font-semibold text-gray-900 leading-tight">
                  {template.name}
                </h3>
                <Badge
                  variant="outline"
                  className={
                    template.status === "Published"
                      ? "bg-green-50 text-green-700 border-green-200 text-xs font-medium"
                      : "bg-amber-50 text-amber-700 border-amber-200 text-xs font-medium"
                  }
                >
                  {template.status}
                </Badge>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-500 mb-5 leading-relaxed">
                {template.description}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-2 mb-5">
                <div className="bg-blue-50/50 rounded-xl p-3 text-center">
                  <FileText className="w-4 h-4 text-blue-500 mx-auto mb-1" />
                  <p className="text-lg font-bold text-blue-700">{template.formsCount}</p>
                  <p className="text-xs text-blue-500 font-medium">Forms</p>
                </div>
                <div className="bg-green-50/50 rounded-xl p-3 text-center">
                  <ShieldCheck className="w-4 h-4 text-green-500 mx-auto mb-1" />
                  <p className="text-lg font-bold text-green-700">{template.policiesCount}</p>
                  <p className="text-xs text-green-500 font-medium">Policies</p>
                </div>
                <div className="bg-purple-50/50 rounded-xl p-3 text-center">
                  <GraduationCap className="w-4 h-4 text-purple-500 mx-auto mb-1" />
                  <p className="text-lg font-bold text-purple-700">{template.learningCount}</p>
                  <p className="text-xs text-purple-500 font-medium">Learning</p>
                </div>
                <div className="bg-amber-50/50 rounded-xl p-3 text-center">
                  <CalendarDays className="w-4 h-4 text-amber-500 mx-auto mb-1" />
                  <p className="text-lg font-bold text-amber-700">{template.activitiesCount}</p>
                  <p className="text-xs text-amber-500 font-medium">Activities</p>
                </div>
              </div>

              {/* Assigned to */}
              <div className="mb-4">
                <p className="text-xs text-gray-400 font-medium mb-2">Assigned to</p>
                <div className="flex flex-wrap gap-2">
                  {(template.assignedTo || []).map((role) => (
                    <span
                      key={role}
                      className="text-xs bg-gray-100 text-gray-700 px-2.5 py-1 rounded-md font-medium"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>

              {/* Updated date */}
              <p className="text-xs text-gray-400 mb-4">
                Updated {formatDate(template.updatedAt)}
              </p>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="sm"
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded-xl gap-2"
                  onClick={() => navigate(`/onboarding/templates/${template.id}`)}
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl px-3"
                  onClick={() => handleDuplicate(template)}
                  disabled={cloningId === template.id}
                >
                  {cloningId === template.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl px-3 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  onClick={() => openDeleteDialog(template)}
                  disabled={deletingId === template.id}
                >
                  {deletingId === template.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <DialogTitle>Delete Template</DialogTitle>
            </div>
            <DialogDescription className="pt-2">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-900">
                {templateToDelete?.name}
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setTemplateToDelete(null);
              }}
              disabled={deletingId !== null}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deletingId !== null}
            >
              {deletingId !== null ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                "Delete Template"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingTemplates;
